import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '../MainLayout';
import { useSession } from '../../contexts/SessionContext';
import { supabase } from '../../lib/supabase_client';
import { Navigate, useNavigate } from 'react-router-dom';
import SourceFilter, { DEFAULT_SOURCE_FILTER, SourceFilterValue } from './SourceFilter';
import '../../styles/page-base.css';

interface MonthlyEntry {
    month: string;
    year: string;
    monthYear: string;
    count: number;
}

interface UsageData {
    todayCount: number;
    monthCount: number;
    yearCount: number;
    allTimeCount: number;
    monthlyData: MonthlyEntry[];
    comparativeMonthlyData: MonthlyEntry[];
    dailyData: Array<{ date: string; fullDate?: string; count: number }>;
}

type TooltipState = {
    x: number;
    y: number;
    text: string;
} | null;

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Current calendar year/month in Asia/Kuala_Lumpur. */
function getMalaysiaNow(): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kuala_Lumpur',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(new Date());
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  return { year, month };
}

function buildYearOptions(currentYear: number): number[] {
  const start = 2024;
  const end = Math.max(currentYear, start);
  const years: number[] = [];
  for (let y = end; y >= start; y--) years.push(y);
  return years;
}

export default function AdminUsageAnalytics() {
  const { isAdmin, isSuperadmin, loading: sessionLoading } = useSession();
  const myNow = getMalaysiaNow();
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [monthlyTooltip, setMonthlyTooltip] = useState<TooltipState>(null);
  const [dailyTooltip, setDailyTooltip] = useState<TooltipState>(null);
  const [selectedCountry, setSelectedCountry] = useState<'MY' | 'SG'>('MY');
  const [selectedSource, setSelectedSource] = useState<SourceFilterValue>(DEFAULT_SOURCE_FILTER);
  const [countries, setCountries] = useState<{iso: string, name: string}[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [showComparative, setShowComparative] = useState(false);
  const [monthlyYear, setMonthlyYear] = useState(myNow.year);
  const [dailyYear, setDailyYear] = useState(myNow.year);
  const [dailyMonth, setDailyMonth] = useState(myNow.month); // 1..12
  const navigate = useNavigate();
  const yearOptions = buildYearOptions(myNow.year);

  const fetchCountries = useCallback(async () => {
    try {
      setLoadingCountries(true);
      const { data, error } = await supabase
        .from('countries')
        .select('iso, name')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching countries:', error);
        return;
      }

      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const fetchUsageData = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.rpc('get_usage_analytics', {
        p_country_iso: selectedCountry,
        p_monthly_year: monthlyYear,
        p_daily_year: dailyYear,
        p_daily_month: dailyMonth,
        p_include_auditplus: selectedSource.auditplus,
        p_include_ocr: selectedSource.ocr,
      });

      if (error) {
        console.error('Error fetching usage analytics:', error);
        throw error;
      }

      if (!data) {
        setUsageData(null);
        return;
      }

      const today = data.today || { count: 0 };
      const thisMonth = data.this_month || { count: 0 };
      const thisYear = data.this_year || { count: 0 };
      const allTime = data.all_time || { count: 0 };
      const parseEntry = (item: any): MonthlyEntry => ({
        month: item.month,
        year: item.year,
        monthYear: item.month_year,
        count: Number(item.count) || 0
      });

      const monthlyData = (data.monthly_data || []).map(parseEntry);
      const comparativeMonthlyData = (data.comparative_monthly_data || []).map(parseEntry);
      const dailyData = (data.daily_data || []).map((item: any) => ({
        date: item.date,
        fullDate: item.full_date,
        count: Number(item.count) || 0
      }));

      setUsageData({
        todayCount: today.count || 0,
        monthCount: thisMonth.count || 0,
        yearCount: Number(thisYear.count) || 0,
        allTimeCount: Number(allTime.count) || 0,
        monthlyData,
        comparativeMonthlyData,
        dailyData
      });
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedCountry, selectedSource, monthlyYear, dailyYear, dailyMonth]);

  useEffect(() => {
    if (!sessionLoading && isAdmin) {
      fetchUsageData();
      fetchCountries();
    }
  }, [sessionLoading, isAdmin, fetchUsageData, fetchCountries]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsageData();
  };

  const handleSalesAnalytics = () => {
    navigate('/admin/sales-analytics');
  };

  // Show loading while checking admin status
  if (sessionLoading || loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 text-sm">Loading...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/signin" replace />;
  }

  if (!usageData) {
    return (
      <MainLayout>
        <div className="p-6">
          <p className="text-gray-600">No usage data available</p>
        </div>
      </MainLayout>
    );
  }

  // Format number without currency symbol (for Y-axis)
  const formatNumber = (amount: number) => {
    if (amount >= 1000) {
      return `${Math.round(amount / 1000)}k`;
    }
    return amount.toString();
  };

  // Calculate nice round maximum for Y-axis based on highest value
  const calculateRoundMax = (maxValue: number, isMonthly: boolean = true) => {
    const isSGD = selectedCountry === 'SG';
    
    if (maxValue === 0) {
      if (isMonthly) {
        return isSGD ? 1000 : 10000;
      } else {
        return isSGD ? 100 : 1000;
      }
    }
    
    if (isMonthly) {
      if (isSGD) {
        // For SGD monthly: smaller increments
        if (maxValue <= 500) return 500;
        if (maxValue <= 1000) return 1000;
        if (maxValue <= 2000) return 2000;
        if (maxValue <= 3000) return 3000;
        if (maxValue <= 4000) return 4000;
        if (maxValue <= 5000) return 5000;
        return Math.ceil(maxValue / 500) * 500;
      } else {
        // For MYR monthly: original values
        if (maxValue <= 1000) return 1000;
        if (maxValue <= 2000) return 2000;
        if (maxValue <= 3000) return 3000;
        if (maxValue <= 4000) return 4000;
        if (maxValue <= 5000) return 5000;
        if (maxValue <= 6000) return 6000;
        if (maxValue <= 7000) return 7000;
        if (maxValue <= 8000) return 8000;
        if (maxValue <= 9000) return 9000;
        if (maxValue <= 10000) return 10000;
        return Math.ceil(maxValue / 500) * 500;
      }
    } else {
      // Daily chart
      if (isSGD) {
        // For SGD daily: smaller increments
        if (maxValue <= 50) return 50;
        if (maxValue <= 100) return 100;
        if (maxValue <= 150) return 150;
        if (maxValue <= 200) return 200;
        return Math.ceil(maxValue / 25) * 25;
      } else {
        // For MYR daily: original values
        if (maxValue <= 100) return 100;
        if (maxValue <= 140) return 140;
        if (maxValue <= 180) return 180;
        if (maxValue <= 200) return 200;
        if (maxValue <= 220) return 220;
        if (maxValue <= 240) return 240;
        if (maxValue <= 260) return 260;
        if (maxValue <= 280) return 280;
        if (maxValue <= 300) return 300;
        if (maxValue <= 320) return 320;
        if (maxValue <= 340) return 340;
        if (maxValue <= 360) return 360;
        if (maxValue <= 380) return 380;
        if (maxValue <= 400) return 400;
        return Math.ceil(maxValue / 50) * 50;
      }
    }
  };

  const calculateYAxisTicks = (maxValue: number, isMonthly: boolean = true) => {
    const roundMax = calculateRoundMax(maxValue, isMonthly);
    
    // Divide by 10 to get 10 evenly spaced intervals (11 ticks total: 0 to 10)
    const interval = roundMax / 10;
    
    // Generate 11 ticks (0, interval, 2*interval, ..., 10*interval)
    const ticks = [];
    for (let i = 0; i <= 10; i++) {
      ticks.push(i * interval);
    }
    
    return { ticks, max: roundMax };
  };
  
  // Chart dimensions
  const monthlyChartHeight = 300;
  const monthlyChartWidth = 600;
  const dailyChartHeight = 300;
  const dailyChartWidth = 600;

  const maxMonthlyCount = showComparative
    ? Math.max(
        ...usageData.monthlyData.map(d => d.count),
        ...usageData.comparativeMonthlyData.map(d => d.count),
        0
      )
    : Math.max(...usageData.monthlyData.map(d => d.count), 0);
  const maxDailyCount = Math.max(...usageData.dailyData.map(d => d.count), 0);
  
  // Calculate Y-axis with 10 intervals
  const monthlyYAxis = calculateYAxisTicks(maxMonthlyCount, true);
  const monthlyMax = monthlyYAxis.max;
  const dailyYAxis = calculateYAxisTicks(maxDailyCount, false);
  const dailyMax = dailyYAxis.max;

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left">
            <p className="text-sm text-gray-500 mb-1">Super Admin only - Form Submission Analytics</p>
            <h1 className="page-title">Usage Analytics Dashboard</h1>
          </div>
          <div className="page-header-right">
            <div className="mr-3">
              <SourceFilter value={selectedSource} onChange={setSelectedSource} />
            </div>
            <select 
              value={selectedCountry} 
              onChange={(e) => setSelectedCountry(e.target.value as 'MY' | 'SG')}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm mr-3"
              disabled={loadingCountries}
            >
              {countries.filter(c => c.iso === 'MY' || c.iso === 'SG').map(country => (
                <option key={country.iso} value={country.iso}>
                  {country.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="f-btn f-btn-success"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh Data</span>
            </button>
            {isSuperadmin ? (
            <button
              onClick={handleSalesAnalytics}
              className="f-btn f-btn-primary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Sales Analytics</span>
            </button>
            ) : null}
          </div>
        </div>

        {/* KPI Cards — four blocks aligned with AdminSalesAnalytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Today's Filings */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-blue-400 mb-2">{usageData.todayCount}</div>
            <div className="text-sm text-gray-600 mb-1">SUBMISSIONS TODAY</div>
          </div>

          {/* This Month */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-blue-400 mb-2">{usageData.monthCount}</div>
            <div className="text-sm text-gray-600 mb-1">SUBMISSIONS THIS MONTH</div>
          </div>

          {/* This calendar year (Asia/Kuala_Lumpur) */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-blue-400 mb-2">{usageData.yearCount.toLocaleString()}</div>
            <div className="text-sm text-gray-600 mb-1">SUBMISSIONS THIS YEAR</div>
            <div className="text-xs text-gray-600 italic">(CALENDAR YEAR {myNow.year})</div>
          </div>

          {/* All-time filings (non-archived, same country filter as other KPIs) */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-green-600 mb-2">{usageData.allTimeCount.toLocaleString()}</div>
            <div className="text-sm text-gray-600 mb-1">ALL-TIME SUBMISSIONS</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Filings Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold">Monthly Filings</h2>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setShowComparative(v => !v)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 select-none"
                  type="button"
                >
                  <span className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${showComparative ? 'bg-slate-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${showComparative ? 'left-4' : 'left-0.5'}`} />
                  </span>
                  Comparative
                </button>
                {showComparative && (
                  <span className="text-xs text-gray-400">({monthlyYear} vs {monthlyYear - 1})</span>
                )}
                <select
                  value={monthlyYear}
                  onChange={(e) => setMonthlyYear(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            {showComparative && (
              <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#f0a8cb' }} />
                  {monthlyYear}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded-sm bg-blue-300" />
                  {monthlyYear - 1}
                </span>
              </div>
            )}
            <div 
              className="relative monthly-chart-container"
              style={{ height: `${monthlyChartHeight + 50}px` }}
            >
            <svg width="100%" height={monthlyChartHeight + 50} viewBox={`0 0 ${monthlyChartWidth} ${monthlyChartHeight + 50}`} preserveAspectRatio="xMidYMid meet">
                {/* Y-axis labels on TOP (numbers only) */}
                {monthlyYAxis.ticks.map((value, index) => {
                  const ratio = value / monthlyMax;
                  return (
                    <text
                      key={index}
                      x="55"
                      y={20 + (monthlyChartHeight - 60) * (1 - ratio) + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-600 font-medium"
                    >
                      {formatNumber(value)}
                    </text>
                  );
                })}

                {/* Grid lines */}
                {monthlyYAxis.ticks.map((value, index) => {
                  const ratio = value / monthlyMax;
                  return (
                    <line
                      key={index}
                      x1="60"
                      y1={20 + (monthlyChartHeight - 60) * (1 - ratio)}
                      x2={monthlyChartWidth - 20}
                      y2={20 + (monthlyChartHeight - 60) * (1 - ratio)}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                  );
                })}

                {/* Bars */}
                {usageData.monthlyData.map((data, index) => {
                  const groupWidth = (monthlyChartWidth - 80) / Math.max(usageData.monthlyData.length, 12);
                  const groupX = 60 + index * groupWidth;
                  const chartBottom = 20 + (monthlyChartHeight - 60);
                  const minHoverHeight = 20;

                  if (showComparative) {
                    const compEntry = usageData.comparativeMonthlyData[index];
                    const singleBar = groupWidth * 0.4;
                    const gap = groupWidth * 0.04;
                    const pairWidth = singleBar * 2 + gap;
                    const pairX = groupX + (groupWidth - pairWidth) / 2;
                    const compX = pairX;
                    const currX = pairX + singleBar + gap;
                    const labelX = groupX + groupWidth / 2;

                    const compCount = compEntry?.count ?? 0;
                    const compBarH = (compCount / monthlyMax) * (monthlyChartHeight - 60);
                    const currBarH = (data.count / monthlyMax) * (monthlyChartHeight - 60);

                    return (
                      <g key={index}>
                        {/* Comparative bar (left, blue) */}
                        <rect
                          x={compX} y={chartBottom - Math.max(compBarH, minHoverHeight)}
                          width={singleBar} height={Math.max(compBarH, minHoverHeight)}
                          fill="transparent" className="cursor-pointer"
                          onMouseEnter={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            const c = e.currentTarget.closest('.monthly-chart-container')?.getBoundingClientRect();
                            if (!c) return;
                            setMonthlyTooltip({ x: r.left + r.width / 2 - c.left, y: r.top - c.top, text: `${compEntry?.monthYear ?? ''}: ${compCount.toLocaleString()} filings` });
                          }}
                          onMouseLeave={() => setMonthlyTooltip(null)}
                        />
                        <rect x={compX} y={chartBottom - compBarH} width={singleBar} height={compBarH} fill="#93c5fd" rx="3" className="pointer-events-none" />

                        {/* Current bar (right, pink) */}
                        <rect
                          x={currX} y={chartBottom - Math.max(currBarH, minHoverHeight)}
                          width={singleBar} height={Math.max(currBarH, minHoverHeight)}
                          fill="transparent" className="cursor-pointer"
                          onMouseEnter={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            const c = e.currentTarget.closest('.monthly-chart-container')?.getBoundingClientRect();
                            if (!c) return;
                            setMonthlyTooltip({ x: r.left + r.width / 2 - c.left, y: r.top - c.top, text: `${data.monthYear}: ${data.count.toLocaleString()} filings` });
                          }}
                          onMouseLeave={() => setMonthlyTooltip(null)}
                        />
                        <rect x={currX} y={chartBottom - currBarH} width={singleBar} height={currBarH} fill="#f0a8cb" rx="3" className="pointer-events-none" />

                        <text x={labelX} y={monthlyChartHeight - 20} textAnchor="middle" className="text-xs fill-gray-700 font-medium pointer-events-none">{data.month}</text>
                      </g>
                    );
                  }

                  // Single bar
                  const barWidth = groupWidth * 0.8;
                  const x = groupX + groupWidth * 0.1;
                  const barHeight = (data.count / monthlyMax) * (monthlyChartHeight - 60);
                  const y = chartBottom - barHeight;
                  const hoverHeight = Math.max(barHeight, minHoverHeight);

                  return (
                    <g key={index}>
                      <rect
                        x={x} y={chartBottom - hoverHeight} width={barWidth} height={hoverHeight}
                        fill="transparent" className="cursor-pointer"
                        onMouseEnter={(e) => {
                          const barRect = e.currentTarget.getBoundingClientRect();
                          const container = e.currentTarget.closest('.monthly-chart-container')?.getBoundingClientRect();
                          if (!container) return;
                          setMonthlyTooltip({ x: barRect.left + barRect.width / 2 - container.left, y: barRect.top - container.top, text: `${data.monthYear}: ${data.count.toLocaleString()} filings` });
                        }}
                        onMouseLeave={() => setMonthlyTooltip(null)}
                      />
                      <rect x={x} y={y} width={barWidth} height={barHeight} fill="#f0a8cb" rx="4" className="pointer-events-none" />
                      <text x={x + barWidth / 2} y={monthlyChartHeight - 20} textAnchor="middle" className="text-xs fill-gray-700 font-medium pointer-events-none">{data.month}</text>
                    </g>
                  );
                })}
              </svg>
              
              {/* Tooltip for monthly chart */}
              {monthlyTooltip && (
                <div
                  className="absolute bg-gray-900 text-white text-xs rounded px-3 py-2 shadow-lg pointer-events-none z-10"
                  style={{
                    left: monthlyTooltip.x,
                    top: monthlyTooltip.y - 8,
                    transform: 'translate(-50%, -100%)'
                  }}
                >
                  {monthlyTooltip.text}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          </div>

          {/* Daily Filings Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <h2 className="text-lg font-semibold">Daily Filings</h2>
              <div className="flex items-center gap-2">
                <select
                  value={dailyMonth}
                  onChange={(e) => setDailyMonth(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  {MONTH_FULL.map((name, i) => (
                    <option key={name} value={i + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={dailyYear}
                  onChange={(e) => setDailyYear(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mb-3 text-xs text-gray-400">
              {MONTH_FULL[dailyMonth - 1]} {dailyYear} · {usageData.dailyData.length} days
            </div>
            <div 
              className="relative daily-chart-container"
              style={{ height: `${dailyChartHeight}px` }}
            >
              <svg width="100%" height={dailyChartHeight} viewBox={`0 0 ${dailyChartWidth} ${dailyChartHeight}`} preserveAspectRatio="xMidYMid meet">
                {/* Y-axis labels on TOP (numbers only) */}
                {dailyYAxis.ticks.map((value, index) => {
                  const ratio = value / dailyMax;
                  return (
                    <text
                      key={index}
                      x="55"
                      y={20 + (dailyChartHeight - 60) * (1 - ratio) + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-600 font-medium"
                    >
                      {formatNumber(value)}
                    </text>
                  );
                })}

                {/* Grid lines */}
                {dailyYAxis.ticks.map((value, index) => {
                  const ratio = value / dailyMax;
                  return (
                    <line
                      key={index}
                      x1="60"
                      y1={20 + (dailyChartHeight - 60) * (1 - ratio)}
                      x2={dailyChartWidth - 20}
                      y2={20 + (dailyChartHeight - 60) * (1 - ratio)}
                      stroke="#e5e7eb"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                  );
                })}

                {/* Bars for daily chart */}
                {usageData.dailyData.map((data, index) => {
                  const barWidth = (dailyChartWidth - 80) / Math.max(usageData.dailyData.length, 1);
                  const barHeight = (data.count / dailyMax) * (dailyChartHeight - 60);
                  const x = 60 + (index * barWidth) + (barWidth * 0.1);
                  const y = 20 + (dailyChartHeight - 60) - barHeight;
                  // Minimum hoverable height (even for zero/small bars)
                  const minHoverHeight = 20;
                  const hoverHeight = Math.max(barHeight, minHoverHeight);
                  const hoverY = 20 + (dailyChartHeight - 60) - hoverHeight;
                  
                  return (
                    <g key={index}>
                      {/* Invisible hover area - always at least minHoverHeight tall */}
                      <rect
                        x={x}
                        y={hoverY}
                        width={barWidth * 0.8}
                        height={hoverHeight}
                        fill="transparent"
                        className="cursor-pointer"
                        onMouseEnter={(e) => {
                          const barRect = e.currentTarget.getBoundingClientRect();
                          const container = e.currentTarget
                            .closest('.daily-chart-container')
                            ?.getBoundingClientRect();
                          
                          if (!container) return;
                          
                          setDailyTooltip({
                            x: barRect.left + barRect.width / 2 - container.left,
                            y: barRect.top - container.top,
                            text: `${data.fullDate || data.date}: ${data.count.toLocaleString()} filings`
                          });
                        }}
                        onMouseLeave={() => setDailyTooltip(null)}
                      />
                      {/* Visible bar */}
                      <rect
                        x={x}
                        y={y}
                        width={barWidth * 0.8}
                        height={barHeight}
                        fill="#93c5fd"
                        rx="4"
                        className="pointer-events-none"
                      />
                      <text
                        x={x + (barWidth * 0.4)}
                        y={dailyChartHeight - 20}
                        textAnchor="middle"
                        className="text-[8px] fill-gray-600 pointer-events-none"
                      >
                        {data.date}
                      </text>
                    </g>
                  );
                })}
              </svg>
              
              {/* Tooltip for daily chart */}
              {dailyTooltip && (
                <div
                  className="absolute bg-gray-900 text-white text-xs rounded px-3 py-2 shadow-lg pointer-events-none z-10"
                  style={{
                    left: dailyTooltip.x,
                    top: dailyTooltip.y - 8,
                    transform: 'translate(-50%, -100%)'
                  }}
                >
                  {dailyTooltip.text}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </MainLayout>
  );
}